import { Button } from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { useSetAtom } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineTextSnippet } from "react-icons/md";

import { usePermissions } from "@/hooks";
import { TransactionStatusPill } from "@/modules/shared";
import { formatWithOffset } from "@/utils";

import { orderDetailModalAtom } from "../atoms/order-detail-atom";
import type { ClaimOrderListItem } from "../dtos/get-claim-order-list-response-dto";
import type { OrderListItem, OrderStatus } from "../types";
import { convertOrderStatusToStatusPill } from "../utils/order-status";

export const useClaimOrderListTableColumns = (page: number) => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimOrderPage.table.columns"
  });
  const setOrderDetailModal = useSetAtom(orderDetailModalAtom);
  const { isSeller, isSuperAdmin } = usePermissions();

  const columns = useMemo<DataTableColumn<ClaimOrderListItem>[]>(
    () => [
      {
        accessor: "no",
        title: t("no"),
        render: (_: ClaimOrderListItem, index: number) =>
          (page - 1) * 10 + index + 1
      },
      {
        accessor: "order_id",
        title: t("orderId")
      },
      ...(isSuperAdmin
        ? [
            {
              accessor: "seller_company_name",
              title: t("seller")
            }
          ]
        : []),
      {
        accessor: "buyer_company_name",
        title: t("buyer")
      },
      {
        accessor: "product_name",
        title: t("product")
      },
      {
        accessor: "item_price",
        title: t("price"),
        render: (record) => record.item_price.toLocaleString("id-ID")
      },
      {
        accessor: "created_at",
        title: t("date"),
        render: (record) => formatWithOffset(record.created_at, "dd MMM yyyy")
      },
      {
        accessor: "order_status",
        title: t("orderStatus"),
        render: (record) => {
          const status = convertOrderStatusToStatusPill(
            record.order_status as OrderStatus
          );
          return (
            <TransactionStatusPill
              status={status}
              customLabel={
                record.order_status === "review"
                  ? record.status_label
                  : undefined
              }
            />
          );
        }
      },
      ...(isSeller || isSuperAdmin
        ? [
            {
              accessor: "actions",
              title: "",
              render: (record: ClaimOrderListItem) => (
                <Button
                  variant="secondary"
                  size="sm"
                  rightSection={<MdOutlineTextSnippet size={14} />}
                  onClick={() =>
                    setOrderDetailModal({
                      visible: true,
                      data: { id: record.order_id } as OrderListItem
                    })
                  }
                >
                  {t("preview")}
                </Button>
              )
            }
          ]
        : [])
    ],
    [page, t, setOrderDetailModal, isSeller, isSuperAdmin]
  );

  return { columns };
};
